var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import Link from "next/link";
import * as React from "react";
import { cn } from "../lib/utils";
import { LoadingSpinner } from "./loading-spinner";
export var buttonSize = {
    default: "h-9 px-4 py-2",
    sm: "h-9 px-3",
    xs: "h-7 px-3 text-xs",
    lg: "h-11 px-8",
    icon: "h-9 w-9",
    "icon-xs": "h-7 w-7 rounded-sm",
    "icon-sm": "h-9 w-9",
    "icon-lg": "h-11 w-11",
};
export var buttonVariant = {
    default: "bg-primary border border-primary/20 text-primary-foreground hover:bg-primary/90",
    destructive: "border border-gray-300/80 bg-background hover:bg-destructive/10 hover:border-destructive text-destructive",
    outline: "border border-gray-300/80 bg-background hover:bg-accent hover:text-accent-foreground",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    link: "text-primary underline-offset-4 hover:underline",
    unstyled: "py-0 px-0 font-normal",
};
export var buttonIsLoading = {
    true: "cursor-progress",
};
export var buttonIsDisabled = {
    true: "cursor-not-allowed",
};
var buttonVariants = cva("inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50", {
    variants: {
        variant: buttonVariant,
        size: buttonSize,
        isLoading: buttonIsLoading,
        isDisabled: buttonIsDisabled,
    },
    defaultVariants: {
        variant: "default",
        size: "default",
        isLoading: false,
        isDisabled: false,
    },
    compoundVariants: [
        {
            variant: "unstyled",
            className: "p-0",
        },
    ],
});
var Button = React.forwardRef(function (_a, ref) {
    var className = _a.className, _b = _a.variant, variant = _b === void 0 ? "default" : _b, _c = _a.size, size = _c === void 0 ? "default" : _c, _d = _a.shadow, shadow = _d === void 0 ? false : _d, _e = _a.asChild, asChild = _e === void 0 ? false : _e, _f = _a.isLoading, isLoading = _f === void 0 ? false : _f, href = _a.href, children = _a.children, disabledMessage = _a.disabledMessage, contentRight = _a.contentRight, contentLeft = _a.contentLeft, props = __rest(_a, ["className", "variant", "size", "shadow", "asChild", "isLoading", "href", "children", "disabledMessage", "contentRight", "contentLeft"]);
    var ButtonComponent = asChild ? Slot : "button";
    var LinkComponent = asChild ? Slot : Link;
    var classNames = cn(buttonVariants({ variant: variant, size: size, className: className, isLoading: isLoading, isDisabled: props.disabled }), "cursor-pointer", shadow && "shadow-sm");
    var content = (_jsx(_Fragment, { children: isLoading ? (_jsxs("div", { className: "relative flex w-full items-center justify-center", children: [_jsx("div", { className: "absolute", children: _jsx(LoadingSpinner, { size: "sm", className: variant === "default" ? "text-white" : "text-primary" }) }), _jsxs("div", { className: "invisible", children: [contentLeft, children, contentRight] })] })) : (_jsxs(_Fragment, { children: [contentLeft, children, contentRight] })) }));
    if (href) {
        return (_jsx(LinkComponent, __assign({ className: classNames, href: href, ref: ref }, props, { children: content })));
    }
    return (_jsx(ButtonComponent, __assign({ className: classNames, ref: ref }, props, { disabled: props.disabled || isLoading, title: props.disabled && disabledMessage ? disabledMessage : undefined, children: content })));
});
Button.displayName = "Button";
export { Button, buttonVariants };
